import React, {PropsWithChildren} from 'react';
import {cva} from '../../Utilities';
import {IconProp} from '../shared/props';

export type HintType = 'default' | 'warning' | 'danger';

export const hintStyles = cva('fv-text-m-regular', {
    variants: {
        type: {
            default: 'text-neutral-60',
            warning: 'text-warning-30',
            danger: 'text-danger-30',
        },
    },
    defaultVariants: {
        type: 'default',
    },
});

export const Hint = ({children, type, truncate}: PropsWithChildren<{type?: HintType; truncate?: boolean}>) => (
    <span
        className={hintStyles({
            type,
            className: {
                'max-w-full overflow-hidden overflow-ellipsis whitespace-nowrap': truncate,
            },
        })}
    >
        {children}
    </span>
);

export const fallbackIcon = {
    element: <div className={'size-full rounded-full bg-neutral-30'}></div>,
};

export function kindOf(thing: unknown) {
    return Object.getPrototypeOf(thing).constructor.name;
}

export function assertIsKey(thing: unknown, message: string): asserts thing is React.Key {
    if (typeof thing !== 'string' && typeof thing !== 'number' && typeof thing !== 'bigint') {
        throw new Error(message);
    }
}

export function propertyAsKey<T>(key: keyof T) {
    return (opt: T) => {
        const val = opt[key];
        assertIsKey(val, `Option property "${String(key)}" of type ${kindOf(val)} cannot be used as key`);
        return val;
    };
}

export function selfAsKey<T>() {
    return (opt: T) => {
        assertIsKey(opt, `Option of type ${kindOf(opt)} cannot be used as key`);
        return opt;
    };
}

export interface OptionObject<T> {
    value: T;
    icon?: IconProp;
    title: string;
    hint?: string;
    hintType?: HintType;
}
